import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Globe, TrendingUp, Zap, Shield, Award, BarChart3, Newspaper, Target, Clock } from 'lucide-react';
import { getPressReleases } from '@/lib/api';
import SEO from '@/components/SEO';

const Home = () => {
  const [trendingReleases, setTrendingReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await getPressReleases();
        setTrendingReleases(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching press releases:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReleases();
  }, []);

  const features = [
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Distribute your press releases to 500+ media outlets, news websites, and journalists worldwide.'
    },
    {
      icon: Zap,
      title: 'Instant Distribution',
      description: 'Go live within minutes. Fast-track your news to thousands of readers instantly.'
    },
    {
      icon: Target,
      title: 'Targeted Distribution',
      description: 'Reach your specific audience by industry, geography, and publication type.'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Track views, engagement, and media pickup with detailed distribution reports.'
    },
    {
      icon: Shield,
      title: 'Editorial Review',
      description: 'Professional review and formatting to meet wire service standards.'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Expert support team available round the clock to assist with your distribution needs.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Media Outlets' },
    { number: '10K+', label: 'Press Releases Distributed' },
    { number: '50+', label: 'Countries Reached' },
    { number: '98%', label: 'Client Satisfaction' }
  ];

  const benefits = [
    'SEO-optimized press release distribution',
    'Google News approved distribution',
    'Social media amplification',
    'Multimedia support (images & videos)',
    'Permanent archive on Ixoraly newsroom',
    'Email alert to targeted journalists'
  ];

  return (
    <div className="bg-white">
      <SEO
        title="Professional Press Release Distribution"
        description="Distribute your press releases to thousands of media outlets, journalists, and search engines worldwide. Affordable PR distribution for startups and enterprises."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-navy-700/50 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                Trusted by businesses worldwide
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-heading">
                Amplify Your Story<br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Reach the Right Audience
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Professional press release distribution platform that connects your news with journalists, media outlets, and audiences worldwide. Get the visibility your story deserves.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/pricing"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Get Started Now
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link
                  to="/about-us"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 border border-white/20"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  data-testid={`stat-${index}`}
                  className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-200"
                >
                  <div className="text-3xl font-bold text-coral-500 mb-2">{stat.number}</div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Ixoraly?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive press release distribution backed by cutting-edge technology and expert support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  data-testid={`feature-${index}`}
                  className="group p-8 bg-white rounded-2xl border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-navy-600 transition-colors duration-300">
                    <Icon className="text-navy-600 group-hover:text-white transition-colors duration-300" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Everything You Need for Successful PR Distribution
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our comprehensive platform provides all the tools and reach necessary to maximize your press release impact.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-green-600 flex-shrink-0" size={24} />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                data-testid="benefits-cta"
                className="inline-flex items-center mt-8 px-8 py-4 bg-navy-600 text-white rounded-lg font-semibold hover:bg-navy-700 transition-all duration-200 shadow-md"
              >
                View Pricing Plans
                <ArrowRight size={20} className="ml-2" />
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Newspaper className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Professional Formatting</h4>
                    <p className="text-sm text-gray-600">Expert editorial review ensures your release meets all wire service standards.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Targeted Journalist Network</h4>
                    <p className="text-sm text-gray-600">Direct access to journalists covering your industry and beat.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">SEO Optimization</h4>
                    <p className="text-sm text-gray-600">Boost your search rankings with optimized distribution and backlinks.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Press Releases */}
      {!loading && trendingReleases.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Recent Press Releases
              </h2>
              <p className="text-lg text-gray-600">
                See what's making headlines on Ixoraly
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {trendingReleases.map((release) => (
                <Link
                  key={release.id}
                  to={`/newsroom/${release.slug}`}
                  data-testid={`featured-release-${release.id}`}
                  className="group bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300"
                >
                  {release.featuredImage && (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={release.featuredImage}
                        alt={release.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mb-3">
                      <span>{new Date(release.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span>•</span>
                      <span>{release.company}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-navy-600 transition-colors line-clamp-2">
                      {release.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4">{release.excerpt}</p>
                    <div className="flex items-center text-navy-600 font-semibold text-sm group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/newsroom"
                data-testid="view-all-releases"
                className="inline-flex items-center px-6 py-3 text-navy-600 border-2 border-navy-600 rounded-lg font-semibold hover:bg-navy-600 hover:text-white transition-all duration-200"
              >
                View All Press Releases
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Amplify Your Message?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join thousands of businesses that trust Ixoraly for their press release distribution needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-press-release"
              data-testid="cta-submit"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Submit Your Press Release
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 border border-white/20"
            >
              Contact Sales Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
